// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  features/financeiro/services/cartaoMovimentoService.ts
// ════════════════════════════════════════════════════════════════════════════

import { apiClient } from '@/lib/apiClient';
import type { PaginadoResponse } from '@/features/financeiro/types';

const BASE = '/api/cartoes/movimentos';

// ── Types ────────────────────────────────────────────────────────────────────

export interface MovCartao {
  id: number;
  cartaoId: number;
  nomeCartao: string;
  dtLancamento: string;
  dtPrevisaoCartao: string;
  dtRecebimento: string | null;
  valorOperacao: number;
  valorTaxa: number;
  valorAdicional: number;
  valorAntecipacao: number;
  valorLiquido: number;
  valorRecebido: number;
  parcela: number;
  qtdParcelas: number;
  nsu: string | null;
  numeroAutenticacaoTef: number | null;
  movDavId: number | null;
  numeroNota: number | null;
  isConciliado: boolean;
  isCancelado: boolean;
  situacaoId: number;
  contaCorrenteRecebimentoId: number | null;
}

export interface CalculoTaxa {
  valorBruto: number;
  parcelas: number;
  taxaPercentual: number;
  valorTaxaPercentual: number;
  valorAdicionalFixo: number;
  taxaAntecipacaoPercentual: number;
  valorAntecipacao: number;
  valorLiquido: number;
  prazoDias: number;
  dtPrevisao: string;
}

export interface LancarMovCartaoRequest {
  cartaoId: number;
  dtLancamento: string;
  valorOperacao: number;
  parcelas: number;
  nsu?: string;
  numeroAutenticacaoTef?: number;
  tid?: string;
  movDavId?: number;
  numeroNota?: number;
  aplicarAntecipacao?: boolean;
  observacao?: string;
}

export interface ConciliarCartoesRequest {
  movCartoesIds: number[];
  contaCorrenteRecebimentoId: number;
  dtRecebimento: string;
  gerarContaPagarTaxa?: boolean;
  fornecedorTaxaId?: number;
  valorRecebidoTotalOverride?: number;
}

/** Totais do filtro inteiro (não só da página). */
export interface MovCartaoTotais {
  bruto: number;
  taxa: number;
  liquido: number;
  recebido: number;
}

export type MovCartaoLista = PaginadoResponse<MovCartao> & { totais: MovCartaoTotais };

export interface ListarMovCartaoParams {
  dtInicio?: string;
  dtFim?: string;
  tipoData?: 'movimento' | 'previsao' | 'recebimento';
  cartaoId?: number;
  operadoraId?: number;
  situacao?: 'pendente' | 'conciliado' | 'cancelado';
  tipoDebCred?: 0 | 1;
  /** NSU, autorização TEF, nº da nota ou valor exato (com vírgula). */
  busca?: string;
  pagina?: number;
  tamanhoPagina?: number;
}

// ── Service ──────────────────────────────────────────────────────────────────

export const cartaoMovimentoService = {
  async listar(params: ListarMovCartaoParams): Promise<MovCartaoLista> {
    const { data } = await apiClient.get<MovCartaoLista>(BASE, { params });
    return data;
  },

  async obterPorId(id: number): Promise<MovCartao> {
    const { data } = await apiClient.get<MovCartao>(`${BASE}/${id}`);
    return data;
  },

  async calcularTaxa(
    cartaoId: number, valorBruto: number, parcelas = 1, aplicarAntecipacao = false,
  ): Promise<CalculoTaxa> {
    const { data } = await apiClient.get<CalculoTaxa>(`${BASE}/calcular-taxa`, {
      params: { cartaoId, valorBruto, parcelas, aplicarAntecipacao },
    });
    return data;
  },

  async lancarManual(req: LancarMovCartaoRequest): Promise<{ id: number }> {
    const { data } = await apiClient.post<{ id: number }>(BASE, req);
    return data;
  },

  async pendentesConciliacao(dtPrevisaoAte?: string, cartaoId?: number): Promise<MovCartao[]> {
    const { data } = await apiClient.get<MovCartao[]>(`${BASE}/pendentes-conciliacao`, {
      params: { dtPrevisaoAte, cartaoId },
    });
    return data;
  },

  async conciliar(req: ConciliarCartoesRequest): Promise<{ conciliados: number; mensagem: string }> {
    const { data } = await apiClient.post(`${BASE}/conciliar`, req);
    return data;
  },

  async desfazer(id: number): Promise<void> {
    await apiClient.post(`${BASE}/${id}/desfazer`);
  },

  async cancelar(id: number, motivo?: string): Promise<void> {
    await apiClient.post(`${BASE}/${id}/cancelar`, { motivo });
  },
};
